import reserved from './vote-slug.reserved.json';

/**
 * Participant link slug (concept-custom-participant-links-2026-10-28 §3, workorder S1 2026-10-28):
 * the short address of a poll, `whocan.org/vote/{slug}`. Shared by app, functions and ogProxy —
 * framework free, the only runtime dependency is `globalThis.crypto` for the random part.
 *
 * The pattern and the hard reserved list are enforced a second time by database.rules.json
 * (`whoo/pollSlugs/$slug`); vote-slug.utils.spec.ts guards that both carry the same regex source.
 */
export const VOTE_SLUG_MIN_LENGTH = 3;
export const VOTE_SLUG_MAX_LENGTH = 40;
/** title part of a proposed slug */
export const VOTE_SLUG_TITLE_MAX_LENGTH = 30;
/** literally the same text as in database.rules.json — no lookaheads, no `(?:` groups (RTDB regex subset) */
export const VOTE_SLUG_PATTERN_SOURCE = '^[a-z0-9]([a-z0-9-]{1,38}[a-z0-9])?$';
/** without l o 0 1 */
export const VOTE_SLUG_RANDOM_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';
export const VOTE_SLUG_RANDOM_SUFFIX_LENGTH = 3;
export const VOTE_SLUG_RANDOM_ONLY_LENGTH = 8;
/** previous slugs that keep resolving; together with the current slug the limit is 3 */
export const VOTE_SLUG_MAX_ALIASES = 2;

export type VoteSlugValidation = 'ok' | 'too-short' | 'too-long' | 'invalid-chars' | 'reserved';

const HARD_RESERVED_VOTE_SLUGS: string[] = reserved.hard;
const SOFT_RESERVED_VOTE_SLUGS: string[] = Object.keys(reserved.soft)
    .reduce((words: string[], language) => words.concat((reserved.soft as Record<string, string[]>)[language]), []);

/** '^(en|de|…|undefined)$' — the hard list as one alternation, used verbatim in database.rules.json */
export const HARD_RESERVED_VOTE_SLUGS_REGEX_SOURCE = '^(' + HARD_RESERVED_VOTE_SLUGS.join('|') + ')$';

const VOTE_SLUG_PATTERN = new RegExp(VOTE_SLUG_PATTERN_SOURCE);
const INVALID_VOTE_SLUG_CHARACTER = /[^a-z0-9-]/;

/**
 * trim → lower case → ä ö ü ß → NFD without combining marks (é è ê ñ ç à …) → æ ø œ →
 * everything else to `-` → collapse and trim hyphens. No length cut.
 */
export function normalizeVoteSlugInput(input: string): string {
    return (input || '')
        .normalize('NFC')
        .trim()
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'o')
        .replace(/œ/g, 'oe')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/** cuts a normalized slug to maxLength at a hyphen; a single word longer than maxLength is cut hard */
function truncateAtWordBoundary(slug: string, maxLength: number): string {
    if (slug.length <= maxLength) {
        return slug;
    }
    if (slug.charAt(maxLength) === '-') {
        return slug.slice(0, maxLength);
    }
    const lastHyphen = slug.lastIndexOf('-', maxLength - 1);
    const truncated = lastHyphen > 0 ? slug.slice(0, lastHyphen) : slug.slice(0, maxLength);
    return truncated.replace(/-+$/, '');
}

export function slugifyTitle(title: string): string {
    return truncateAtWordBoundary(normalizeVoteSlugInput(title), VOTE_SLUG_TITLE_MAX_LENGTH);
}

export function isHardReservedVoteSlug(slug: string): boolean {
    return HARD_RESERVED_VOTE_SLUGS.indexOf(slug) >= 0;
}

/** whole-slug match only: `sextett-probe` is fine although `sex` is on the list */
export function isSoftReservedVoteSlug(slug: string): boolean {
    return SOFT_RESERVED_VOTE_SLUGS.indexOf(slug) >= 0;
}

/**
 * Order: characters outside a-z 0-9 - → reserved (hard or soft) → length → pattern (edge hyphen, `--`).
 * Characters and reserved words come before the length so that `ä` reads as invalid-chars and `de` as
 * reserved, not both as too-short.
 */
export function validateVoteSlug(slug: string): VoteSlugValidation {
    const value = slug || '';
    if (INVALID_VOTE_SLUG_CHARACTER.test(value)) {
        return 'invalid-chars';
    }
    if (isHardReservedVoteSlug(value) || isSoftReservedVoteSlug(value)) {
        return 'reserved';
    }
    if (value.length < VOTE_SLUG_MIN_LENGTH) {
        return 'too-short';
    }
    if (value.length > VOTE_SLUG_MAX_LENGTH) {
        return 'too-long';
    }
    if (!VOTE_SLUG_PATTERN.test(value) || value.indexOf('--') >= 0) {
        return 'invalid-chars';
    }
    return 'ok';
}

/**
 * May this slug be looked up in `whoo/pollSlugs`? Format (length, charset, edge hyphen, `--`) and not on the
 * hard list — the soft list is a client-side brake on CREATING a slug and must not block the resolution of
 * an existing poll (workorder S2). Shared by the ogProxy parser and the app resolver (S3); always check
 * before a database path is built from the value.
 */
export function isResolvableVoteSlug(slug: string): boolean {
    const value = slug || '';
    return value.length >= VOTE_SLUG_MIN_LENGTH
        && value.length <= VOTE_SLUG_MAX_LENGTH
        && VOTE_SLUG_PATTERN.test(value)
        && value.indexOf('--') < 0
        && !isHardReservedVoteSlug(value);
}

/** uniform integer in [0, maxExclusive) from crypto.getRandomValues — throws when crypto is missing, no Math.random fallback */
function cryptoRandomInt(maxExclusive: number): number {
    const cryptoApi = typeof globalThis !== 'undefined'
        ? (globalThis as { crypto?: { getRandomValues?: (array: Uint32Array) => Uint32Array } }).crypto
        : undefined;
    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
        throw new Error('vote slug: globalThis.crypto.getRandomValues is not available');
    }
    const limit = Math.floor(0x100000000 / maxExclusive) * maxExclusive;
    const buffer = new Uint32Array(1);
    do {
        cryptoApi.getRandomValues(buffer);
    } while (buffer[0] >= limit);
    return buffer[0] % maxExclusive;
}

export function randomVoteSlugPart(length: number, randomInt?: (maxExclusive: number) => number): string {
    const nextInt = randomInt || cryptoRandomInt;
    let part = '';
    for (let position = 0; position < length; position++) {
        part += VOTE_SLUG_RANDOM_ALPHABET.charAt(nextInt(VOTE_SLUG_RANDOM_ALPHABET.length));
    }
    return part;
}

/** `{title slug, max 30}-{3 random}`; no usable title (empty, only emojis or symbols) → 8 random characters */
export function proposeVoteSlug(title: string | null | undefined, randomInt?: (maxExclusive: number) => number): string {
    const titleSlug = slugifyTitle(title || '');
    if (!titleSlug) {
        return randomVoteSlugPart(VOTE_SLUG_RANDOM_ONLY_LENGTH, randomInt);
    }
    return titleSlug + '-' + randomVoteSlugPart(VOTE_SLUG_RANDOM_SUFFIX_LENGTH, randomInt);
}

/**
 * Exactly three candidates for a taken slug: `{slug}-2`, `{slug}-{year}`, `{slug}-{3 random}`, each base cut at
 * a hyphen so the candidate stays within 40 characters. Availability is the caller's job (index read, S3).
 */
export function alternativeVoteSlugs(slug: string, year: number, randomInt?: (maxExclusive: number) => number): string[] {
    const base = normalizeVoteSlugInput(slug) || randomVoteSlugPart(VOTE_SLUG_RANDOM_ONLY_LENGTH, randomInt);
    const suffixes = ['2', String(year), randomVoteSlugPart(VOTE_SLUG_RANDOM_SUFFIX_LENGTH, randomInt)];
    return suffixes.map((suffix) => truncateAtWordBoundary(base, VOTE_SLUG_MAX_LENGTH - suffix.length - 1) + '-' + suffix);
}
